import { useCallback, useMemo, useState } from 'react';

import {
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  ProfileOutlined,
  SafetyOutlined,
  GlobalOutlined,
  CheckOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import { Tag, Avatar, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';

import { useColorSchemeDropdownItems } from '@/components/colorScheme/ColorSchemeSwitcher';
import { env } from '@/config/env';

import { useAuth } from '../hooks/useAuth';
import { ROLE_BADGE_COLORS, ROLE_DISPLAY_NAMES } from '../utils/roleRedirect';

import { ChangePasswordModal } from './ChangePasswordModal';
import { ProfileEditModal } from './ProfileEditModal';

interface UserBadgeProps {
  className?: string;
  dropdownPlacement?:
    | 'bottomLeft'
    | 'bottomRight'
    | 'bottomCenter'
    | 'topLeft'
    | 'topRight'
    | 'topCenter';
}

export function UserBadge({ className = '', dropdownPlacement = 'bottomRight' }: UserBadgeProps) {
  const { user, isLoading, logout, hasPermission } = useAuth();
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const closeDropdown = useCallback(() => setIsOpen(false), []);
  const themeItems = useColorSchemeDropdownItems({ onItemClick: closeDropdown });
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const resolvedLang = i18n.resolvedLanguage === 'en' ? 'en' : 'vi';

  const roleDisplayName = user
    ? t(`auth.role.${user.role}`) || ROLE_DISPLAY_NAMES[user.role] || user.role
    : '';
  const roleColor = user ? ROLE_BADGE_COLORS[user.role] || 'default' : 'default';

  const headerDisplayName = user
    ? user.name?.trim() ||
      (env.DEMO_MASK_USER_EMAIL ? t('userMenu.guestUserLabel', 'User') : user.email)
    : '';

  const menuItems = useMemo((): MenuProps['items'] => {
    if (!user) return [];

    const languageChildren: MenuProps['items'] = [
      {
        key: 'lang-en',
        label: (
          <span className="flex min-w-[140px] items-center justify-between gap-2 text-xs">
            <span>{t('userMenu.languageEnglish', 'English')}</span>
            {resolvedLang === 'en' ? (
              <CheckOutlined className="text-cyan-600 dark:text-cyan-400" />
            ) : null}
          </span>
        ),
        onClick: () => {
          void i18n.changeLanguage('en');
          setIsOpen(false);
        },
      },
      {
        key: 'lang-vi',
        label: (
          <span className="flex min-w-[140px] items-center justify-between gap-2 text-xs">
            <span>{t('userMenu.languageVietnamese', 'Tiếng Việt')}</span>
            {resolvedLang === 'vi' ? (
              <CheckOutlined className="text-cyan-600 dark:text-cyan-400" />
            ) : null}
          </span>
        ),
        onClick: () => {
          void i18n.changeLanguage('vi');
          setIsOpen(false);
        },
      },
    ];

    const items: MenuProps['items'] = [
      {
        key: 'profile-header',
        icon: <UserOutlined />,
        label: headerDisplayName,
        disabled: true,
      },
      {
        key: 'role-badge',
        icon: <SafetyOutlined />,
        label: <Tag color={roleColor}>{roleDisplayName}</Tag>,
        disabled: true,
      },
      { type: 'divider', key: 'divider-1' },
      {
        key: 'profile',
        icon: <ProfileOutlined />,
        label: t('userMenu.profile', 'Personal Information'),
        onClick: () => {
          setIsOpen(false);
          setProfileModalOpen(true);
        },
      },
    ];

    if (user.role === 'admin' || hasPermission('settings:view')) {
      items.push({
        key: 'settings',
        icon: <SettingOutlined />,
        label: t('userMenu.settings', 'Account Settings'),
        onClick: () => {
          setIsOpen(false);
          setChangePasswordOpen(true);
        },
      });
    }

    items.push(
      {
        key: 'appearance',
        icon: <BulbOutlined />,
        label: t('userMenu.appearance', 'Giao diện'),
        children: themeItems,
      },
      {
        key: 'language',
        icon: <GlobalOutlined />,
        label: t('userMenu.language', 'Language'),
        children: languageChildren,
      },
      { type: 'divider', key: 'divider-2' },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: t('userMenu.logout', 'Logout'),
        danger: true,
        onClick: async () => {
          setIsOpen(false);
          await logout();
        },
      },
    );

    return items;
  }, [
    user,
    headerDisplayName,
    t,
    i18n,
    resolvedLang,
    roleDisplayName,
    roleColor,
    hasPermission,
    logout,
    themeItems,
  ]);

  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Avatar size="small" icon={<UserOutlined />} className="bg-gray-400" />
        <div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className={`relative inline-block ${className}`}>
      <Dropdown
        menu={{ items: menuItems }}
        placement={dropdownPlacement}
        trigger={['click']}
        open={isOpen}
        onOpenChange={setIsOpen}
        classNames={{ root: 'user-dropdown-overlay' }}
      >
        <button
          type="button"
          role="button"
          aria-haspopup="true"
          aria-expanded={isOpen}
          aria-label={t('userMenu.ariaLabel', 'User menu')}
          className="flex items-center gap-2 rounded-lg px-3 py-1.5 transition-all duration-200 hover:bg-[var(--mdm-header-tint)] focus:outline-none focus:ring-2 focus:ring-cyan-500/50 dark:hover:bg-white/10"
        >
          <Avatar
            size="small"
            icon={<UserOutlined />}
            src={user.avatar}
            className="cursor-pointer border-2 border-transparent transition-colors hover:border-cyan-400"
          />

          <div className="flex flex-col items-start">
            <span className="max-w-[120px] truncate text-xs font-medium text-slate-900 dark:text-white">
              {headerDisplayName}
            </span>
            <Tag color={roleColor} className="my-0 text-[10px] leading-tight">
              {roleDisplayName}
            </Tag>
          </div>

          <svg
            className={`ml-1 h-4 w-4 text-slate-400 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </Dropdown>

      {isOpen && (
        <div className="sr-only" role="status" aria-live="polite">
          {t('userMenu.menuOpened', 'User menu opened. Press Escape to close.')}
        </div>
      )}

      <ProfileEditModal
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        initialDisplayName={user.name}
        initialEmail={user.email}
      />
      <ChangePasswordModal open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
    </div>
  );
}
